import {
  List,
  Create,
  Edit,
  SimpleForm,
  TextInput,
  TextField,
  Show,
  TabbedShowLayout,
  Tab,
  Resource,
  DateField,
  DateInput,
  useNotify,
  Filter, FunctionField, useRefresh, useDataProvider,
  Datagrid,
  BooleanField,
  BooleanInput,
  useEditController,
  useInput,
  NumberInput,
  SelectInput,
  TopToolbar,
  EditButton,
} from "react-admin";
import { Button, InputAdornment } from "@material-ui/core";
import { hostname } from "../providers/api";
import { useTokenOps } from "../components/TokenContext";
import { JsonField, JsonInput } from "react-admin-json-view";
import { JSONValidator } from "../utils/validators/jsonvalidator";
import { useEffect, useState } from "react";
import { useForm } from "react-final-form";
import Toggle from "../components/toggle/toggle";
import { ECOBALYSE_OFFICIAL_API_KEY, ACCOUNT_BUSINESS_CHOICES } from "../consts";
import { useParams } from 'react-router-dom';
import AccountCarbonUpload from "./account/AccountCarbonUpload";

import { v4 as uuidv4 } from 'uuid';

// Helper function to mask API key for security
const maskApiKey = (key) => {
  if (!key || key.length < 8) return '••••••••';
  const firstPart = key.substring(0, 4);
  const lastPart = key.substring(key.length - 4);
  return `${firstPart}••••••••${lastPart}`;
};

// ─── Circularity / Ecobalyse account params (IT-3177) ───────────────────────────

// Backend enum default, shown in the list when business is empty.
const BUSINESS_DEFAULT = "large-business-without-services";

// Segments of product_range_breadth (jsonb { women, men, kids, baby, underwear, others }).
const PRODUCT_RANGE_SEGMENTS = [
  { key: "women", label: "Women", short: "W" },
  { key: "men", label: "Men", short: "M" },
  { key: "kids", label: "Kids", short: "K" },
  { key: "baby", label: "Baby", short: "B" },
  { key: "underwear", label: "Underwear", short: "U" },
  { key: "others", label: "Others", short: "O" },
];

const BUSINESS_DESCRIPTION =
  "Type of business and repair services offered. Used by Ecobalyse to compute the durability coefficient.";
const PRODUCT_RANGE_BREADTH_DESCRIPTION =
  "Number of different products per segment. Used by Ecobalyse to compute the durability coefficient.";
const SPARE_PARTS_DESCRIPTION =
  "Does the brand offer spare parts or replacement components?";

// ─── DPP / AGEC account params (IT-3177) ────────────────────────────────────────
// brand_tagline y brand_story quedaron fuera del contrato: el backend los eliminó
// de Account (son platform-owned, no se persisten via este endpoint).
const PACKAGING_GUIDE_URL_DESCRIPTION =
  "Link to the brand's packaging sorting guide (AGEC).";

// Maps the business enum to its readable label; empty falls back to the default (AC).
const renderBusiness = (record) => {
  const value = record.business || BUSINESS_DEFAULT;
  const choice = ACCOUNT_BUSINESS_CHOICES.find((c) => c.id === value);
  return choice ? choice.name : value;
};

// Compact summary of product_range_breadth, e.g. "W:120 / M:80"; "N/A" if empty.
const renderProductRangeBreadth = (record) => {
  const prb = record.product_range_breadth;
  if (!prb) return "N/A";
  const parts = PRODUCT_RANGE_SEGMENTS
    .filter((s) => prb[s.key] != null && prb[s.key] !== "")
    .map((s) => `${s.short}:${prb[s.key]}`);
  return parts.length ? parts.join(" / ") : "N/A";
};

const renderSparePartsYN = (record) => (record.spare_parts_availability ? "Y" : "N");

// Per-segment integer inputs for product_range_breadth (jsonb), used in Create/Edit.
const ProductRangeBreadthInputs = () => (
  <>
    <div style={{ width: "100%", marginTop: 12, marginBottom: 4 }}>
      <div style={{ fontWeight: 600, fontSize: "0.9rem" }}>Product breadth range per segment</div>
      <div style={{ fontSize: "0.8125rem", color: "rgba(0, 0, 0, 0.6)" }}>
        {PRODUCT_RANGE_BREADTH_DESCRIPTION}
      </div>
    </div>
    {PRODUCT_RANGE_SEGMENTS.map((seg) => (
      <NumberInput
        key={seg.key}
        source={`product_range_breadth.${seg.key}`}
        label={seg.label}
        min={0}
        step={1}
      />
    ))}
  </>
);

const accountTokenRequest = (tokenOps, accountId) => () => {
  if (tokenOps.rootToken?.data?.accountId === accountId) {
    tokenOps.exitAccountToken();
  } else {
    const request = new Request(hostname + "/auth/switch/" + accountId, {
      method: "POST",
      headers: new Headers({
        "Content-Type": "application/json",
        "authorization": "Bearer " + tokenOps.root.accessToken
      }),
    });
    return fetch(request)
      .then((response) => {
        if (response.status < 200 || response.status >= 300) {
          throw new Error(response.statusText);
        }
        return response.json();
      })
      .then(tokens => {
        tokenOps.setAccountToken(tokens);
      });
  };
};

const Filters = (props) => (
  <Filter {...props}>
    <TextInput label="Search" source="name" alwaysOn />
  </Filter>
);

const AccountsList = (props) => {
  const refresh = useRefresh();
  const notify = useNotify();
  const dataProvider = useDataProvider()

  const handleVisibility = (e) => {
    e.stopPropagation();
  };

  const handleCopyUrl = (url, e) => {
    e.stopPropagation();
    if (url) {
      navigator.clipboard.writeText(url).then(() => {
        notify("URL copiada al portapapeles", "success");
      }).catch(() => {
        notify("Error al copiar URL", "warning");
      });
    }
  };

  const updateViewAccount = (id, inactive) => {

    dataProvider.update('accounts', {
      id: id,
      data: { inactive: !inactive },
      previousData: { id: id, inactive: inactive }
    })
      .then((response) => {
        if (response.statusCode && response.statusCode >= 400) {
          notify(`Error: ${response.statusText}`, "warning");
        } else {
          refresh();
          notify("Updated successfully", "success");
        }
      });
  };

  return (
    <List {...props} filters={Filters(props)}>
      <div style={{ marginTop: '15px' }}>
        <Datagrid rowClick="show" >
          <TextField source="name" />
          <TextField source="id" />
          <TextField source="refUsed" label="References used" />
          <FunctionField
            label="References available"
            render={record => record.totalReferences - record.refUsed}
          />
          <TextField source="totalReferences" />
          <TextField source="historicalReference" label="Historical references" />
          <FunctionField
            onClick={handleVisibility}
            label="Account visibility"
            source="inactive"
            render={(item) => (
              <Toggle
                checked={!item.inactive}
                onChange={() => updateViewAccount(item.id, item.inactive)}
                className="ml-2"
              />
            )}
          />
          <FunctionField
            source="logoUrl"
            label="Logo image url"
            render={record => (
              <div
                onClick={(e) => handleCopyUrl(record.logoUrl, e)}
                title="Click para copiar URL completa"
                style={{
                  maxWidth: '200px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  cursor: 'pointer',
                  color: '#1976d2',
                  textDecoration: 'underline'
                }}
              >
                {record.logoUrl || ''}
              </div>
            )}
          />
          <DateField source="contractStartDate" />
          <BooleanField source="apiIntegration" label="API Integration (Side Lateral)" />
          <TextField source="apiIntegrationKey" label="API Integration Key" />
          <FunctionField
            label="Ecobalyse API Key (Official)"
            render={record => <EcobalyseApiKeyField record={record} fieldName="ecobalyseApiKey" />}
          />
          <FunctionField
            label="Ecobalyse API Key (Test)"
            render={record => <EcobalyseApiKeyField record={record} fieldName="ecobalyseApiKeyTest" />}
          />
          <TextField source="ecobalyseBrandId" label="Ecobalyse Brand ID" />
          {/* Circularity / Ecobalyse params (IT-3177) */}
          <FunctionField label="Type of brand (Ecobalyse)" render={renderBusiness} />
          <FunctionField label="Product breadth range per segment" render={renderProductRangeBreadth} />
          <FunctionField label="Spare parts availability" render={renderSparePartsYN} />
          {/* DPP / AGEC params (IT-3177) */}
          <TextField source="packaging_guide_url" label="Packaging guide URL" emptyText="N/A" />
          <DateField source="expiration" />
          <DateField source="createdAt" label="Creation date" />

        </Datagrid>
      </div>
    </List>
  )
};

const AccountShowActions = ({ basePath, data }) => {
  const tokenOps = useTokenOps();
  const isWrongAccount = data?.id !== tokenOps.account?.data?.accountId;

  return (
    <TopToolbar>
      <EditButton
        basePath={basePath}
        record={data}
        disabled={isWrongAccount}
        sx={{
          opacity: isWrongAccount ? 0.5 : 1,
          '&.Mui-disabled': {
            color: 'rgba(0, 0, 0, 0.26)'
          }
        }}
      />
    </TopToolbar>
  );
};

// Component to display Ecobalyse API Key with conditional logic
const EcobalyseApiKeyField = ({ record, fieldName = 'ecobalyseApiKey' }) => {
  if (!record) return <div>...</div>;

  // Check if Ecobalyse feature is active by looking at accountFeatureKeys
  const hasEcobalyseFeature = record.accountFeatureKeys?.some(
    feature => feature.key === 'ecobalyse'
  );

  // Determine the status and value to display
  let displayValue = 'N/A';
  let textColor = '#00000061'; // Gray for N/A
  let fontWeight = 400;

  if (!hasEcobalyseFeature) {
    // Feature not linked to account
    displayValue = 'N/A';
    textColor = '#00000061';
  } else if (!record[fieldName] || record[fieldName] === '') {
    // Feature active but no API key
    displayValue = 'Pending';
    textColor = '#ff9800'; // Orange for Pending
  } else {
    // Feature active and API key exists - show masked key
    displayValue = maskApiKey(record[fieldName]);
    textColor = '#000000de'; // Dark for active
    fontWeight = 400;
  }

  return (
    <div style={{
      fontSize: '14px',
      color: textColor,
      fontWeight: fontWeight
    }}>
      {displayValue}
    </div>
  );
};

export const AccountsShow = (props) => {
  const notify = useNotify();
  const [state, setState] = useState('ready');
  const tokenOps = useTokenOps();

  const installPresetViews = () => {
    setState('loading');

    fetch(hostname + '/views/installPresetView', {
      method: 'POST',
      body: JSON.stringify({ "accountId": tokenOps.account?.data.accountId }),
      headers: {
        "Content-Type": "application/json",
        'authorization': 'Bearer ' + tokenOps.account?.accessToken,
      }
    }).then(({ status, statusText }) => {
      setState('done');
      if (status < 400) {
        notify('views installed successfully', 'success');
      } else {
        notify(`Error: ${statusText}`, 'warning')
      }
    })
  };

  const deletePresetViews = () => {
    setState('loading');

    fetch(hostname + '/views/deletePresetView', {
      method: 'POST',
      body: JSON.stringify({ "accountId": tokenOps.account?.data.accountId }),
      headers: {
        "Content-Type": "application/json",
        'authorization': 'Bearer ' + tokenOps.account?.accessToken,
      }
    }).then(({ status, statusText }) => {
      setState('done');
      if (status < 400) {
        notify('views deleted successfully', 'success');
      } else {
        notify(`Error: ${statusText}`, 'warning')
      }
    })
  };

  return (
    <Show {...props} actions={<AccountShowActions />}>
      <TabbedShowLayout>
        <Tab label="Account details">
        <TextField source="name" />
        <TextField source="id" />
        <DateField source="contractStartDate" />
        <DateField source="expiration" />
        <TextField source="refUsed" label="References used" />
        <FunctionField
          label="References available"
          render={record => record.totalReferences - record.refUsed}
        />
        <TextField source="totalReferences" />
        <TextField source="historicalReference" label="Historical references" />
        <TextField source="eCommerceDomain" />
        <TextField source="second_hand_programme" label="Collection point" emptyText="" />
        <BooleanField source="apiIntegration" label="API Integration (Side Lateral)" />
        <TextField source="apiIntegrationKey" label="API Integration Key" />
        <FunctionField
          label="Ecobalyse API Key (Official)"
          render={record => <EcobalyseApiKeyField record={record} fieldName="ecobalyseApiKey" />}
        />
        <FunctionField
          label="Ecobalyse API Key (Test)"
          render={record => <EcobalyseApiKeyField record={record} fieldName="ecobalyseApiKeyTest" />}
        />
        <TextField source="ecobalyseBrandId" label="Ecobalyse Brand ID" />
        {/* Circularity / Ecobalyse params (IT-3177) */}
        <FunctionField label="Type of brand (Ecobalyse)" render={renderBusiness} />
        <FunctionField label="Product breadth range per segment" render={renderProductRangeBreadth} />
        <FunctionField label="Spare parts availability" render={renderSparePartsYN} />
        {/* DPP / AGEC params (IT-3177) */}
        <TextField source="packaging_guide_url" label="Packaging guide URL" emptyText="" />
        <DateField source="createdAt" label="Creation date" />
        {tokenOps.account?.data.accountId === props.id &&
          (<>
            <Button color="primary"
              variant="contained"
              style={{ marginTop: '10px', marginBottom: '10px' }}
              onClick={installPresetViews}
              disabled={state === 'state'}
              component="label">
              Install preset views
            </Button>
            <Button color="primary"
              variant="contained"
              style={{ color: 'white', backgroundColor: 'red', margin: '20px' }}
              onClick={deletePresetViews}
              disabled={state === 'state'}
              component="label">
              Delete preset views
            </Button>
          </>
          )
        }
        <JsonField
          source="eCommerceCustomization"
          addLabel={true}
          jsonString={false} // Set to true if the value is a string, default: false
          reactJsonOptions={{
            // Props passed to react-json-view
            name: null,
            collapsed: true,
            enableClipboard: false,
            displayDataTypes: true,
          }}
        />
        <TextField source="eCommerceDomain" />

        {!tokenOps.isSwitchAccount && <Button color="primary" onClick={accountTokenRequest(tokenOps, props.id)}>Work in this account</Button>}
        {tokenOps.isSwitchAccount && <Button color="primary" onClick={tokenOps.exitAccountToken}>Return to BCome Internal</Button>}

        </Tab>
        <Tab label="Carbon upload" path="carbon-upload">
          <AccountCarbonUpload />
        </Tab>
      </TabbedShowLayout>
    </Show>
  );
}

const EcobalyseOfficialApiKeyInput = ({ style }) => {
  const form = useForm();
  return (
    <TextInput
      source="ecobalyseApiKey"
      label="Ecobalyse API Key (Official)"
      style={style || { width: 400 }}
      required={false}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <Button
              size="small"
              onClick={() => form.change('ecobalyseApiKey', ECOBALYSE_OFFICIAL_API_KEY)}
            >
              Set default
            </Button>
          </InputAdornment>
        ),
      }}
    />
  );
};

const CreateAccount = (props) => {
  return (
    <Create {...props}>
      <SimpleForm>
        <TextInput source="name" required={true} />
        <TextInput source="logoUrl" required={false} />
        <BooleanInput source="apiIntegration" label="API Integration (Side Lateral)" />
        <DateInput source="contractStartDate" defaultValue={getStartOfToday()} />
        <NumberInput source="totalReferences" defaultValue={0} />
        <TextInput source="eCommerceDomain" />
        <EcobalyseOfficialApiKeyInput />
        <TextInput
          source="ecobalyseApiKeyTest"
          label="Ecobalyse API Key (Test)"
          required={false}
        />
        <TextInput
          source="ecobalyseBrandId"
          label="Ecobalyse Brand ID"
          required={false}
        />
        {/* Circularity / Ecobalyse params (IT-3177) */}
        <SelectInput
          source="business"
          label="Type of brand (Ecobalyse)"
          choices={ACCOUNT_BUSINESS_CHOICES}
          defaultValue={BUSINESS_DEFAULT}
          helperText={BUSINESS_DESCRIPTION}
        />
        <ProductRangeBreadthInputs />
        <BooleanInput
          source="spare_parts_availability"
          label="Spare parts availability"
          defaultValue={false}
          helperText={SPARE_PARTS_DESCRIPTION}
        />
        {/* DPP / AGEC params (IT-3177) */}
        <TextInput
          source="packaging_guide_url"
          label="Packaging guide URL"
          required={false}
          helperText={PACKAGING_GUIDE_URL_DESCRIPTION}
        />
        <TextInput source="refAvailable" style={{ visibility: 'hidden' }} defaultValue={0} />
      </SimpleForm>
    </Create>
  )
};

const getStartOfToday = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Set the time to midnight
  return today;
};

const CustomTextFieldWithButton = ({ input, source, label, ...rest }) => {
  const tokenOps = useTokenOps();
  const { id } = useParams();
  const notify = useNotify();
  const refresh = useRefresh();
  const [loading, setLoading] = useState(false);

  const handleClick = async () => {
    setLoading(true);
    const data = JSON.stringify({ refUsed: 0, contractStartDate: getStartOfToday() });
    fetch(hostname + "/accounts/" + id, {
      method: "PATCH",
      body: data,
      headers: {
        "Content-type": "application/json",
        authorization: "Bearer " + tokenOps.account?.accessToken,
      },
    }).then((response) => {
      if (response.status < 400) {
        refresh();
        notify("Updated successfully", "success");
      } else {
        notify(`Error: ${response.statusText}`, "warning");
      }
    })
      .finally(() => setLoading(false));
  };

  return (
    <div>
      <label className="reference-used" htmlFor={source}>
        <span>{label}</span>
      </label>
      <div className="account-reset-references">
        <TextInput type="number" {...input} source={source} {...rest} label="" helperText={false} />
        <Button
          variant="contained"
          component="label"
          onClick={handleClick}
          disabled={loading}
        >
          {loading ? 'Loading...' : 'Reset references'}
        </Button>
      </div>

    </div>
  );
};

const CustomTextInput = ({ label, source, value, disabled }) => {
  const {
    input: { name, onChange, ...restInput },
    meta: { touched, error },
  } = useInput({ source });

  // Update the field value manually when the prop changes
  useEffect(() => {
    onChange(value);
  }, [value, onChange]);

  return (
    <TextInput
      label={label}
      name={name}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      error={touched && !!error}
      helperText={touched && error}
      style={{ width: 330 }}
      {...restInput}
    />
  );
};

const EditAccount = (props) => {
  const [apiKey, setApiKey] = useState(null);
  const [loading, setLoading] = useState(false);
  const { record, isLoading } = useEditController(props);
  const [apiIntegration, setApiIntegration] = useState(record.apiIntegration);

  if (isLoading) return <div>Loading...</div>;

  const handleApiKey = () => {
    setLoading(true);
    if (!record.apiIntegrationKey) {
      setApiKey(uuidv4());
    }
    setLoading(false);
  }

  return (
    <Edit {...props}>
      <SimpleForm>
        <TextInput source="name" />
        <TextInput source="logoUrl" required={false} />
        <DateInput source="contractStartDate" />
        <DateInput source="expiration" />
        <TextInput source="totalReferences" defaultValue={0} />
        <TextField source="historicalReference" label="Historical references" />
        <CustomTextFieldWithButton source="refUsed" label="References used" />
        <FunctionField
          label="References available"
          render={record => record.totalReferences - record.refUsed}
        />
        <JsonInput
          source="eCommerceCustomization"
          validate={[JSONValidator]}
          jsonString={false}
          reactJsonOptions={{
            name: null,
            collapsed: true,
            enableClipboard: false,
            displayDataTypes: true,
          }}
        />
        <TextInput source="eCommerceDomain" required={false} />
        {/* DPP - End of Life: el backend alimenta end_of_life.collection_point_url
            desde second_hand_programme (Account.collection_point_url quedó legacy).
            Acepta URL o texto libre; nullable y sin validación en FE. */}
        <TextInput source="second_hand_programme" label="Collection point" required={false} />
        <BooleanInput source="apiIntegration" label="API Integration (Side Lateral)" onChange={() => setApiIntegration(!apiIntegration)} />
        {
          record?.apiIntegrationKey
            ? <TextInput style={{ width: 330 }} source="apiIntegrationKey" disabled label="API Integration Key" />
            : <>
              <CustomTextInput
                source="apiIntegrationKey"
                label="API Integration Key"
                value={apiKey}
                disabled
              />
              <div className="account-reset-references">
                <Button
                  variant="contained"
                  style={{ marginTop: '10px', marginBottom: '10px' }}
                  component="label"
                  onClick={handleApiKey}
                  disabled={loading || !apiIntegration}
                >
                  {loading ? 'Loading...' : 'Generate API Key'}
                </Button>
              </div>
            </>
        }
        {/* Ecobalyse API Keys */}
        <EcobalyseOfficialApiKeyInput />
        <TextInput
          source="ecobalyseApiKeyTest"
          label="Ecobalyse API Key (Test)"
          style={{ width: 330 }}
        />
        <TextInput
          source="ecobalyseBrandId"
          label="Ecobalyse Brand ID"
          style={{ width: 330 }}
        />
        {/* Circularity / Ecobalyse params (IT-3177) */}
        <SelectInput
          source="business"
          label="Type of brand (Ecobalyse)"
          choices={ACCOUNT_BUSINESS_CHOICES}
          defaultValue={BUSINESS_DEFAULT}
          helperText={BUSINESS_DESCRIPTION}
        />
        <ProductRangeBreadthInputs />
        <BooleanInput
          source="spare_parts_availability"
          label="Spare parts availability"
          defaultValue={false}
          helperText={SPARE_PARTS_DESCRIPTION}
        />
        {/* DPP / AGEC params (IT-3177) */}
        <TextInput
          source="packaging_guide_url"
          label="Packaging guide URL"
          required={false}
          helperText={PACKAGING_GUIDE_URL_DESCRIPTION}
        />
      </SimpleForm>
    </Edit>
  );
};

const accountsResource = () => <Resource
  name="accounts"
  options={{ label: 'Accounts', menuGroup: 'Accounts' }}
  list={AccountsList}
  create={CreateAccount}
  edit={EditAccount}
  show={AccountsShow} />;

export default accountsResource;
